import * as ToggleGroup from '@radix-ui/react-toggle-group'
import { useEffect, useState } from 'react'
import { useTheme } from '@opallabs/hooks'
import {
  Button as LapidaryButton,
  ButtonProps as LapidaryButtonProps,
} from '../Button'
import { Variants } from '../ButtonVariant/variants'
import * as styles from './styles'
import { ToggleButtonProvider, useToggleButton } from './ToggleButtonContext'

type RootProps = {
  initialValue: string
  offVariant: Variants
  onValueChange: (value: string) => void
  onVariant: Variants
}
export { type RootProps as ToggleButtonGroupProps }

const Root: React.FC<React.PropsWithChildren<RootProps>> = ({
  children,
  initialValue,
  offVariant,
  onValueChange,
  onVariant,
}) => {
  const theme = useTheme()
  const [value, setValue] = useState(initialValue)

  // If initialValue changes, sync internal state
  useEffect(() => {
    if (initialValue !== value) {
      setValue(initialValue)
    }
  }, [initialValue, value])

  const handleValueChange = (newValue: string) => {
    if (newValue === '' || newValue === value) return
    setValue(newValue)
    onValueChange?.(newValue)
  }

  return (
    <ToggleGroup.Root
      css={[
        styles.toggleButtonContainer,
        styles.toggleButtonContainerVariant(theme, offVariant),
      ]}
      onValueChange={handleValueChange}
      type="single"
      value={value}
    >
      <ToggleButtonProvider
        offVariant={offVariant}
        onVariant={onVariant}
        value={value}
      >
        {children}
      </ToggleButtonProvider>
    </ToggleGroup.Root>
  )
}
Root.displayName = 'Lapidary: ToggleButtonGroup.Root'

type ButtonProps = LapidaryButtonProps & {
  value: string
}

const Button: React.VFC<ButtonProps> = ({ value, ...buttonProps }) => {
  const { onVariant, offVariant, value: currentValue } = useToggleButton()
  const selected = value === currentValue

  return (
    <ToggleGroup.Item css={styles.toggleButton} asChild value={value}>
      <LapidaryButton
        {...buttonProps}
        variant={selected ? onVariant : offVariant}
      />
    </ToggleGroup.Item>
  )
}
Button.displayName = 'Lapidary: ToggleButtonGroup.Button'

export const ToggleButtonGroup = {
  Root,
  Button,
}
